import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { projectTaskService } from '../../services/projectTaskService';
import { Plus, X, Calendar, FileText, Loader2, GripVertical, Archive, RefreshCw, Trash2, Clock, AlertCircle, CheckSquare, TrendingUp, Target, Sparkles } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';





export default function ProjectManagementDashboard() {
  // Tab state
  const [activeTab, setActiveTab] = useState('active');
  
  // Task states
  const [tasks, setTasks] = useState({ to_do: [], in_progress: [], completed: [] });
  const [statistics, setStatistics] = useState({ total: 0, toDo: 0, inProgress: 0, completed: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({ taskName: '', description: '', dueDate: '' });
  const [activeId, setActiveId] = useState(null);

  // Archived tasks states
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [sortBy, setSortBy] = useState('archiveDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);

  // Compute filtered archived tasks before using it in functions
  const filteredArchivedTasks = archivedTasks
    ?.filter(task =>
      task?.taskName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      task?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
    ?.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'archiveDate':
          comparison = new Date(a?.archivedAt) - new Date(b?.archivedAt);
          break;
        case 'taskName':
          comparison = a?.taskName?.localeCompare(b?.taskName);
          break;
        case 'daysRemaining':
          comparison = (a?.daysUntilDeletion || 0) - (b?.daysUntilDeletion || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Helper function to check if a task is overdue
  const isTaskOverdue = (task) => {
    if (!task?.dueDate || task?.status === 'completed') return false;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const dueDate = new Date(task.dueDate);
    dueDate?.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Count overdue tasks
  const countOverdueTasks = () => {
    const allActiveTasks = [...tasks?.to_do, ...tasks?.in_progress];
    return allActiveTasks?.filter(task => isTaskOverdue(task))?.length;
  };

  useEffect(() => {
    loadTasks();
    loadStatistics();
    loadArchivedTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const [toDo, inProgress, completed] = await Promise.all([
        projectTaskService?.getByStatus('to_do'),
        projectTaskService?.getByStatus('in_progress'),
        projectTaskService?.getByStatus('completed')
      ]);
      
      setTasks({ 
        to_do: toDo?.filter(t => !t?.archivedAt), 
        in_progress: inProgress?.filter(t => !t?.archivedAt), 
        completed: completed?.filter(t => !t?.archivedAt) 
      });
      setError('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await projectTaskService?.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  // Archived tasks functions
  const loadArchivedTasks = async () => {
    try {
      const tasks = await projectTaskService?.getArchived();
      setArchivedTasks(tasks);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleRestore = async (taskId) => {
    try {
      await projectTaskService?.restore(taskId);
      loadTasks();
      loadStatistics();
      loadArchivedTasks();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selectedTasks?.map(id => projectTaskService?.restore(id)));
      setSelectedTasks([]);
      loadTasks();
      loadStatistics();
      loadArchivedTasks();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedTasks?.length} tasks?`)) return;
    try {
      await Promise.all(selectedTasks?.map(id => projectTaskService?.delete(id)));
      setSelectedTasks([]);
      loadArchivedTasks();
    } catch (err) {
      setError(err?.message);
    }
  };

  const toggleSelectTask = (taskId) => {
    setSelectedTasks(prev =>
      prev?.includes(taskId) ? prev?.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTasks?.length === filteredArchivedTasks?.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(filteredArchivedTasks?.map(t => t?.id));
    }
  };

  const getDeletionWarningColor = (days) => {
    if (days <= 6) return 'text-red-600 bg-red-50';
    if (days <= 14) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddTask = async (e) => {
    e?.preventDefault();
    try {
      await projectTaskService?.create({ ...formData, status: 'to_do' });
      setFormData({ taskName: '', description: '', dueDate: '' });
      setShowAddModal(false);
      loadTasks();
      loadStatistics();
    } catch (err) {
      // Check if it's the 100 task limit error
      if (err?.message?.includes('Active task limit reached') || err?.message?.includes('100 active tasks')) {
        setError('You have reached the maximum of 100 active tasks. Please archive or delete some tasks before creating new ones.');
      } else {
        setError(err?.message);
      }
    }
  };

  const handleUpdateTask = async (e) => {
    e?.preventDefault();
    try {
      await projectTaskService?.update(editingTask?.id, formData);
      setEditingTask(null);
      setFormData({ taskName: '', description: '', dueDate: '' });
      loadTasks();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await projectTaskService?.update(taskId, { status: newStatus });
      loadTasks();
      loadStatistics();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await projectTaskService?.delete(taskId);
      loadTasks();
      loadStatistics();
      loadArchivedTasks();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleArchiveTask = async (taskId) => {
    try {
      await projectTaskService?.archive(taskId);
      
      // Immediately remove the archived task from the UI
      setTasks(prevTasks => ({
        ...prevTasks,
        completed: prevTasks?.completed?.filter(t => t?.id !== taskId)
      }));
      
      // Update statistics
      setStatistics(prevStats => ({
        ...prevStats,
        total: prevStats?.total - 1,
        completed: prevStats?.completed - 1,
        archived: prevStats?.archived + 1
      }));
      
      // Reload archived tasks to show the newly archived task
      loadArchivedTasks();
    } catch (err) {
      setError(err?.message);
      // Reload tasks if there was an error to restore correct state
      loadTasks();
      loadStatistics();
    }
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({ taskName: task?.taskName, description: task?.description || '', dueDate: task?.dueDate || '' });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingTask(null);
    setFormData({ taskName: '', description: '', dueDate: '' });
  };

  const handleDragStart = (event) => {
    setActiveId(event?.active?.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeTask = findTaskById(active?.id);
    
    if (!activeTask) {
      setActiveId(null);
      return;
    }

    // Determine the target status more reliably
    // First try to get the droppable zone ID directly
    let overContainer = over?.id;
    
    // If we dropped on a task, get its status from the task data
    if (over?.data?.current?.sortable?.containerId) {
      overContainer = over?.data?.current?.sortable?.containerId;
    }

    // Validate that overContainer is a valid status enum value
    const validStatuses = ['to_do', 'in_progress', 'completed'];
    if (!validStatuses?.includes(overContainer)) {
      // If invalid, try to find the task we dropped on and use its status
      const overTask = findTaskById(over?.id);
      if (overTask) {
        overContainer = overTask?.status;
      } else {
        // If still can't determine valid status, cancel the operation
        console.error('Invalid drop target:', overContainer);
        setActiveId(null);
        return;
      }
    }

    // Check if we're moving to a different status column
    if (overContainer && overContainer !== activeTask?.status) {
      const oldStatus = activeTask?.status;
      const newStatus = overContainer;
      
      // Double-check newStatus is valid before proceeding
      if (!validStatuses?.includes(newStatus)) {
        console.error('Invalid status value:', newStatus);
        setActiveId(null);
        return;
      }

      const updatedTask = { ...activeTask, status: newStatus };

      // Optimistically update UI
      setTasks(prevTasks => {
        const newTasks = { ...prevTasks };
        newTasks[oldStatus] = newTasks?.[oldStatus]?.filter(t => t?.id !== activeTask?.id);
        newTasks[newStatus] = [...(newTasks?.[newStatus] || []), updatedTask];
        return newTasks;
      });

      setStatistics(prevStats => ({
        ...prevStats,
        toDo: prevStats?.toDo + (newStatus === 'to_do' ? 1 : 0) - (oldStatus === 'to_do' ? 1 : 0),
        inProgress: prevStats?.inProgress + (newStatus === 'in_progress' ? 1 : 0) - (oldStatus === 'in_progress' ? 1 : 0),
        completed: prevStats?.completed + (newStatus === 'completed' ? 1 : 0) - (oldStatus === 'completed' ? 1 : 0),
      }));

      // Sync with backend
      try {
        await projectTaskService?.update(activeTask?.id, { status: newStatus });
      } catch (err) {
        setError(err?.message);
        // Reload to restore correct state on error
        loadTasks();
        loadStatistics();
      }
    } else if (active?.id !== over?.id) {
      // Reordering within the same column
      const oldIndex = tasks?.[activeTask?.status]?.findIndex(task => task?.id === active?.id);
      const newIndex = tasks?.[activeTask?.status]?.findIndex(task => task?.id === over?.id);

      setTasks(prevTasks => ({
        ...prevTasks,
        [activeTask?.status]: arrayMove(prevTasks?.[activeTask?.status], oldIndex, newIndex),
      }));
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const findTaskById = (id) => {
    for (const status in tasks) {
      const task = tasks?.[status]?.find(t => t?.id === id);
      if (task) return task;
    }
    return null;
  };

  // Enhanced SortableTaskCard with modern design
  const SortableTaskCard = ({ task }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task?.id });

    const style = {
      transform: CSS?.Transform?.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case 'to_do':
          return 'To Do';
        case 'in_progress':
          return 'In Progress';
        case 'completed':
          return 'Completed';
        default:
          return status;
      }
    };

    const statusLabel = getStatusLabel(task?.status);
    const showArchiveButton = task?.status === 'completed';
    const isOverdue = isTaskOverdue(task);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group bg-white rounded-xl p-5 mb-3 border-2 transition-all duration-200 hover:shadow-lg ${
          isOverdue 
            ? 'border-red-300 shadow-red-100' 
            : 'border-gray-100 hover:border-blue-200 shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div 
            {...attributes}
            {...listeners}
            className="flex items-center gap-3 flex-1 cursor-move"
          >
            <div className="p-2 rounded-lg bg-gray-50 group-hover:bg-blue-50 transition-colors">
              <GripVertical className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-base mb-1">{task?.taskName}</h4>
              {isOverdue && (
                <span className="inline-flex items-center gap-1.5 text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full font-medium">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Overdue
                </span>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleDeleteTask(task?.id);
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {task?.description && (
          <div className="flex items-start gap-2 mb-4 ml-14">
            <FileText className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
            <p className="text-sm text-gray-600 leading-relaxed">{task?.description}</p>
          </div>
        )}
        
        {task?.dueDate && (
          <div className={`flex items-center gap-2 text-sm mb-4 ml-14 ${
            isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'
          }`}>
            <Calendar className="w-4 h-4" />
            <span>Due: {new Date(task.dueDate)?.toLocaleDateString()}</span>
          </div>
        )}
        
        <div className="flex gap-2 ml-14 flex-wrap items-center">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              openEditModal(task);
            }}
            className="text-sm bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors font-medium"
          >
            Edit Task
          </button>
          <span className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            {statusLabel}
          </span>
          {showArchiveButton && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                handleArchiveTask(task?.id);
              }}
              className="text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all flex items-center gap-1.5 font-medium shadow-sm"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          )}
        </div>
      </div>
    );
  };

  // Enhanced DroppableColumn with modern gradient design
  const DroppableColumn = ({ title, tasks, status, color, icon: IconComponent, gradient }) => {
    const { setNodeRef } = useDroppable({
      id: status,
    });

    return (
      <div className="flex-1 min-w-[320px]">
        <div className={`bg-gradient-to-br ${gradient} rounded-t-2xl p-5 shadow-sm`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
              <IconComponent className={`w-5 h-5 text-${color}-600`} />
            </div>
            <h3 className={`font-bold text-${color}-900 text-lg`}>{title}</h3>
          </div>
          <span className={`text-sm text-${color}-700 font-medium`}>{tasks?.length} tasks</span>
        </div>
        <SortableContext
          items={tasks?.map(t => t?.id) || []}
          strategy={verticalListSortingStrategy}
        >
          <div ref={setNodeRef} className="bg-gradient-to-b from-gray-50 to-gray-100/50 rounded-b-2xl p-4 min-h-[500px] border-2 border-t-0 border-gray-100">
            {tasks?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="p-4 bg-gray-200 rounded-full mb-3">
                  <IconComponent className={`w-8 h-8 text-${color}-400`} />
                </div>
                <p className="text-gray-400 text-center font-medium">
                  Drag and drop tasks here
                </p>
              </div>
            ) : (
              tasks?.map(task => (
                <SortableTaskCard key={task?.id} task={task} />
              ))
            )}
          </div>
        </SortableContext>
      </div>
    );
  };

  const activeTask = activeId ? findTaskById(activeId) : null;

  // Tab definitions
  const tabs = [
    { id: 'active', label: 'Active Tasks', icon: CheckSquare },
    { id: 'archived', label: 'Archived Tasks', icon: Archive }
  ];

  return (
    <>
      <Helmet>
        <title>Project Management | FinanceFlow</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
        <Sidebar />
        <Header />
        <main className="ml-64 mt-16 px-6 pb-6 pt-4">
          {/* Modern Page Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl border border-blue-400/20 p-8 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h1 className="text-3xl font-bold text-white">Project Management</h1>
                </div>
                <p className="text-blue-100 text-base ml-16">
                  Organize, track, and complete your tasks with ease
                </p>
              </div>
              {activeTab === 'active' && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-white text-blue-600 px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-50 transition-all shadow-lg font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Add Task
                </button>
              )}
              {activeTab === 'archived' && (
                <button
                  onClick={loadArchivedTasks}
                  className="bg-white text-blue-600 px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-50 transition-all shadow-lg font-semibold"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 mb-6 overflow-hidden">
            <div className="flex border-b border-gray-200">
              {tabs?.map((tab) => {
                const TabIcon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2.5 px-8 py-5 text-sm font-semibold transition-all relative ${
                      activeTab === tab?.id
                        ? 'text-blue-600 bg-blue-50/50' :'text-slate-600 hover:text-slate-900 hover:bg-gray-50'
                    }`}
                  >
                    <TabIcon className="w-5 h-5" />
                    {tab?.label}
                    {activeTab === tab?.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Enhanced Error Message */}
          {error && (
            <div className="bg-gradient-to-r from-red-50 to-red-100/50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-xl mb-4 shadow-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* ACTIVE TASKS TAB */}
          {activeTab === 'active' && (
            <div className="space-y-6">
              {/* Enhanced Statistics with gradients and icons */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl p-5 shadow-lg border border-slate-500/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-slate-300" />
                      <div className="text-sm text-slate-300 font-medium">Total Tasks</div>
                    </div>
                    <div className="text-3xl font-bold text-white">{statistics?.total}</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 shadow-lg border border-blue-400/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-200" />
                      <div className="text-sm text-blue-100 font-medium">To Do</div>
                    </div>
                    <div className="text-3xl font-bold text-white">{statistics?.toDo}</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl p-5 shadow-lg border border-yellow-400/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-yellow-100" />
                      <div className="text-sm text-yellow-100 font-medium">In Progress</div>
                    </div>
                    <div className="text-3xl font-bold text-white">{statistics?.inProgress}</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg border border-green-400/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckSquare className="w-4 h-4 text-green-100" />
                      <div className="text-sm text-green-100 font-medium">Completed</div>
                    </div>
                    <div className="text-3xl font-bold text-white">{statistics?.completed}</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 shadow-lg border border-red-400/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-100" />
                      <div className="text-sm text-red-100 font-medium">Overdue</div>
                    </div>
                    <div className="text-3xl font-bold text-white">{countOverdueTasks()}</div>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-500 font-medium">Loading your tasks...</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <div className="flex gap-5 overflow-x-auto pb-4">
                    <DroppableColumn
                      title="To Do"
                      tasks={tasks?.to_do}
                      status="to_do"
                      color="blue"
                      icon={Sparkles}
                      gradient="from-blue-100 to-blue-200"
                    />
                    <DroppableColumn
                      title="In Progress"
                      tasks={tasks?.in_progress}
                      status="in_progress"
                      color="yellow"
                      icon={Clock}
                      gradient="from-yellow-100 to-amber-200"
                    />
                    <DroppableColumn
                      title="Completed"
                      tasks={tasks?.completed}
                      status="completed"
                      color="green"
                      icon={CheckSquare}
                      gradient="from-green-100 to-emerald-200"
                    />
                  </div>
                  <DragOverlay>
                    {activeTask ? (
                      <div className="bg-white rounded-xl p-5 shadow-2xl border-2 border-blue-500 opacity-95 transform scale-105">
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <h4 className="font-semibold text-gray-900">{activeTask?.taskName}</h4>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </div>
          )}

          {/* ARCHIVED TASKS TAB - Enhanced styling */}
          {activeTab === 'archived' && (
            <div className="space-y-6">
              {selectedTasks?.length > 0 && (
                <div className="flex items-center gap-3 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-sm">
                  <span className="text-sm font-semibold text-blue-900">
                    {selectedTasks?.length} selected
                  </span>
                  <button
                    onClick={handleBulkRestore}
                    className="text-sm bg-white text-blue-600 px-4 py-2 rounded-lg border-2 border-blue-300 hover:bg-blue-50 transition-colors font-medium shadow-sm"
                  >
                    Restore Selected
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="text-sm bg-white text-red-600 px-4 py-2 rounded-lg border-2 border-red-300 hover:bg-red-50 transition-colors font-medium shadow-sm"
                  >
                    Delete Selected
                  </button>
                </div>
              )}

              {/* Enhanced Search and Filter */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="archiveDate">Archive Date</option>
                    <option value="taskName">Task Name</option>
                    <option value="daysRemaining">Days Remaining</option>
                  </select>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e?.target?.value)}
                    className="px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>

              {/* Enhanced Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl p-5 shadow-lg">
                  <div className="text-sm text-slate-300 mb-2 font-medium">Total Archived</div>
                  <div className="text-3xl font-bold text-white">{archivedTasks?.length}</div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 shadow-lg">
                  <div className="text-sm text-red-100 mb-2 font-medium">Expiring Soon (≤7 days)</div>
                  <div className="text-3xl font-bold text-white">
                    {archivedTasks?.filter(t => (t?.daysUntilDeletion || 0) <= 7)?.length}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl p-5 shadow-lg">
                  <div className="text-sm text-yellow-100 mb-2 font-medium">Warning (7-14 days)</div>
                  <div className="text-3xl font-bold text-white">
                    {archivedTasks?.filter(t => {
                      const days = t?.daysUntilDeletion || 0;
                      return days > 7 && days <= 14;
                    })?.length}
                  </div>
                </div>
              </div>

              {/* Enhanced Archived Tasks Table */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden">
                {filteredArchivedTasks?.length === 0 ? (
                  <div className="p-16 text-center">
                    <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                      <Archive className="w-12 h-12 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-lg font-medium">No archived tasks found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b-2 border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left">
                            <input
                              type="checkbox"
                              checked={selectedTasks?.length === filteredArchivedTasks?.length}
                              onChange={toggleSelectAll}
                              className="rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Task Name</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Description</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Archived Date</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Days Remaining</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filteredArchivedTasks?.map((task) => (
                          <tr key={task?.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="px-6 py-5">
                              <input
                                type="checkbox"
                                checked={selectedTasks?.includes(task?.id)}
                                onChange={() => toggleSelectTask(task?.id)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-6 py-5">
                              <div className="font-semibold text-slate-900">{task?.taskName}</div>
                              {task?.dueDate && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5">
                                  <Calendar className="w-3.5 h-3.5" />
                                  Due: {new Date(task.dueDate)?.toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-5">
                              <p className="text-sm text-slate-600 max-w-md truncate">
                                {task?.description || '-'}
                              </p>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Clock className="w-4 h-4" />
                                {new Date(task?.archivedAt)?.toLocaleDateString()}
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getDeletionWarningColor(task?.daysUntilDeletion || 0)}`}>
                                {task?.daysUntilDeletion || 0} days
                              </span>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleRestore(task?.id)}
                                  className="text-sm bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1.5 font-medium"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Restore
                                </button>
                                <button
                                  onClick={() => handleDeleteTask(task?.id)}
                                  className="text-sm bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1.5 font-medium"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Enhanced Archival Info */}
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl shadow-lg border-2 border-blue-200/50 p-8">
                <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <AlertCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  Archive Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                    <h4 className="font-bold text-blue-900 mb-3 text-base">🗑️ Auto-Deletion</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Archived tasks are automatically deleted after 30 days. Restore important tasks before the deadline.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                    <h4 className="font-bold text-blue-900 mb-3 text-base">💾 Task Limit</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      System maintains maximum 30 active tasks. Oldest tasks are auto-archived when limit is reached.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Add/Edit Task Modal */}
          {(showAddModal || editingTask) && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingTask ? 'Edit Task' : 'Add New Task'}
                  </h3>
                  <button 
                    onClick={closeModal} 
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={editingTask ? handleUpdateTask : handleAddTask}>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Task Name *</label>
                      <input
                        type="text"
                        value={formData?.taskName}
                        onChange={(e) => setFormData({ ...formData, taskName: e?.target?.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        required
                        placeholder="Enter task name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                      <textarea
                        value={formData?.description}
                        onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
                        rows="3"
                        placeholder="Add task description (optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date</label>
                      <input
                        type="date"
                        value={formData?.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e?.target?.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-8">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg font-semibold"
                    >
                      {editingTask ? 'Update Task' : 'Add Task'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}